import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from '../../Router';
import { Button } from '@vacasa/react-components-lib';
import { IconStarB } from '../../assests/icons';
import { ReactComponent as PropertyReviewIllustration } from '../../assests/images/property_review.svg';
import './RatingSelection.scss';

export const RatingSelection: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [selectedRating, setSelectedRating] = useState<number | undefined>();
    const [hoverRating, setHoverRating] = useState<number | undefined>();

    const reservationId = searchParams.get('reservation');
    const confirmationCode = searchParams.get('confirmation') ?? '';
    const nsuHash = searchParams.get('nsu') ?? '';
    const isPal = searchParams.get('pal') === 'true' || searchParams.get('pal') === '1';
    const withContent = searchParams.get('with_content') === 'true' || searchParams.get('with_content') === '1';
    const reviewRequestOrigin = searchParams.get('review_request_origin') ?? '';

    const onMouseEnter = (index: number): void => {
        setHoverRating(index);
    };

    const onMouseLeave = (): void => {
        setHoverRating(undefined);
    };

    const onSaveRating = (index: number): void => {
        setSelectedRating(index);
    };

    const handleContinue = (): void => {
        if (!selectedRating || !reservationId) {
            return;
        }

        // Calculate disposition based on rating: 1-3 stars = 0 (negative), 4-5 stars = 1 (positive)
        const calculatedDisposition = selectedRating >= 4 ? '1' : '0';

        // Build the survey URL with the selected rating and calculated disposition
        const surveyParams = new URLSearchParams();
        surveyParams.set('reservation', reservationId);
        if (confirmationCode) surveyParams.set('confirmation', confirmationCode);
        if (nsuHash) surveyParams.set('nsu', nsuHash);
        surveyParams.set('disposition', calculatedDisposition);
        surveyParams.set('star-rating', selectedRating.toString());
        if (isPal) surveyParams.set('pal', 'true');
        if (withContent) surveyParams.set('with_content', 'true');
        if (reviewRequestOrigin) surveyParams.set('review_request_origin', reviewRequestOrigin);

        navigate(`${AppRoutes.SURVEY}?${surveyParams.toString()}`);
    };

    const getValuesInRange = (min: number, max: number): Array<number> => {
        const rows = [];
        for (let i = min; i <= max; i++) rows.push(i);
        return rows;
    };

    return (
        <div className="rating-selection-container">
            <div className="rating-selection-modal">
                <div className="rating-selection-content">
                    <div className="rating-selection-illustration">
                        <PropertyReviewIllustration className="property-review-illustration-svg" />
                    </div>

                    <h1 className="base-font-title rating-selection-title">Review your stay</h1>
                    <p className="base-font-body rating-selection-prompt">How was your experience? Your feedback is important to us.</p>

                    <div className="star-rating-selection">
                        <div className="star-rating">
                            {getValuesInRange(1, 5).map((index) => {
                                const isHoveringAboveCurrent = hoverRating !== undefined && hoverRating >= index;
                                const isBelowCurrentRating = selectedRating !== undefined && selectedRating >= index;
                                const fill = isHoveringAboveCurrent || isBelowCurrentRating ? '#FFD000' : 'none';

                                return (
                                    <div
                                        key={index}
                                        className="rating-indicator"
                                        onMouseEnter={() => onMouseEnter(index)}
                                        onMouseLeave={onMouseLeave}
                                        onClick={() => onSaveRating(index)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onSaveRating(index);
                                            }
                                        }}
                                    >
                                        <label style={{ cursor: 'pointer' }}>
                                            <IconStarB fill={fill} />
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {!selectedRating && <p className="base-font-body rating-selection-instruction">To begin, choose a star value.</p>}

                    {selectedRating && (
                        <div className="rating-selection-actions">
                            <Button customClass="body-regular" variant="primary" onClick={handleContinue} disabled={!selectedRating}>
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
